import { useMemo } from 'react'

import { NewInboxTasksQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { TASK_CATEGORY_TRANSLATIONS, getTaskCategoryEnumValues } from './constants'

type CategoryCounts = NonNullable<NewInboxTasksQuery['tasks']['categoryCounts']>

type Params = {
  taskCategoryPresets?: CategoryCounts
  /** Initial category counts for the filter (should not change when filters are applied) */
  initialCategoryCounts?: CategoryCounts
}

export const usePresets = ({ taskCategoryPresets, initialCategoryCounts }: Params) => {
  const categoryCounts = taskCategoryPresets
  const filterCategoryCounts = initialCategoryCounts ?? categoryCounts

  const presets = useMemo(
    () =>
      getTaskCategoryEnumValues()
        .map((value) => {
          const category = categoryCounts?.find((count) => count.category === value)

          if (!category) return

          return {
            label: TASK_CATEGORY_TRANSLATIONS[category.category]?.(),
            filter: { category: [value] },
            itemsCount: () => category?.count ?? 0,
          }
        })
        .filter((preset) => preset && preset.itemsCount() > 0)
        .filter(Boolean),
    [categoryCounts]
  )

  const categoryFilter = useMemo(
    () => ({
      label: i18n.t('tasks.filter.category'),
      type: 'in' as const,
      options: {
        options: getTaskCategoryEnumValues()
          .map((value) => {
            const category = filterCategoryCounts?.find((count) => count.category === value)

            if (!category) return

            return {
              label: TASK_CATEGORY_TRANSLATIONS[category.category]?.(),
              value,
              itemsCount: () => (category ? (category?.count ?? 0) : 0),
            }
          })
          .filter((option) => option && option.itemsCount() > 0)
          .filter(Boolean),
      },
    }),
    [filterCategoryCounts]
  )

  return {
    presets,
    categoryFilter,
  }
}
